import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import {
  ApiError,
  listProducts,
  listSchedules,
  updateSchedule,
  type ProductSummary,
  type ScheduleSummary,
} from '../lib/api-client';
import { schedulableProducts } from '../lib/schedulable-products';

/**
 * 班次管理列表頁（AC-A2-1 / AC-A2-3 / AC-A2-5）。
 *
 * 重編（管理性）：
 *  - 期間過濾：起～迄日期 + 快捷（今天 / 未來7天 / 本月 / 未來30天 / 全部）。
 *    預設只顯示「今天起」之班次（隱藏過去，去除最大宗雜訊）。
 *  - 狀態過濾：全部 / 僅營運 / 僅停航。
 *  - 依「出發日」分組：每日一個日標頭（星期 + 當日彙總：班數 / 容量Σ / 已售Σ /
 *    剩餘Σ / 停航數），底下列各時段；解決長列表難定位特定日的問題。
 *  - 過濾條件全寫入 URL searchParams，可分享 / 重整保留。
 *
 * 後端 ListSchedulesQueryDto 已支援 departure_after / departure_before /
 * weather_cancelled，故為純前端編排 + 既有參數串接。
 */

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

const pad = (n: number): string => String(n).padStart(2, '0');

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDaysStr(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y!, m! - 1, d!);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

/** 本地 YYYY-MM-DD 之當日 00:00 → ISO（期間下界，含）。 */
function startOfDayIso(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 0, 0, 0, 0).toISOString();
}

/** ISO → 本地 YYYY-MM-DD（分組鍵）。 */
function localDateKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function weekdayOf(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return WEEKDAY_LABELS[new Date(y!, m! - 1, d!).getDay()]!;
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type StatusFilter = 'all' | 'active' | 'cancelled';

interface DayGroup {
  date: string;
  items: ScheduleSummary[];
  capacity: number;
  sold: number;
  remaining: number;
  cancelled: number;
}

export function SchedulesListPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const productId = searchParams.get('product_id') ?? '';
  // 預設：今天起（隱藏過去），迄日開放
  const from = searchParams.get('from') ?? todayStr();
  const to = searchParams.get('to') ?? '';
  const status = (searchParams.get('status') as StatusFilter | null) ?? 'all';
  const enabled = productId !== '';

  const products = useQuery({
    queryKey: ['products.list'],
    queryFn: () => listProducts({ limit: 200 }),
  });
  const productOptions = useMemo<ProductSummary[]>(
    () => products.data?.items ?? [],
    [products.data],
  );

  const weatherCancelled = status === 'cancelled' ? true : status === 'active' ? false : undefined;
  const schedules = useQuery({
    queryKey: ['schedules.list', productId, from, to, status],
    queryFn: () =>
      listSchedules(productId, {
        limit: 500,
        departureAfter: from === '' ? undefined : startOfDayIso(from),
        // 迄日「含當日」→ 上界取 (迄日+1) 之 00:00（BE 為 departure_at < before）
        departureBefore: to === '' ? undefined : startOfDayIso(addDaysStr(to, 1)),
        weatherCancelled,
      }),
    enabled,
  });

  const qc = useQueryClient();
  const toggleCancel = useMutation({
    mutationFn: ({ id, next }: { id: string; next: boolean }) =>
      updateSchedule(id, { weather_cancelled: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['schedules.list'] });
    },
  });

  // ── 依出發日分組（BE 已按 departure_at asc 排序）──
  const groups = useMemo<DayGroup[]>(() => {
    const map = new Map<string, DayGroup>();
    for (const s of schedules.data?.items ?? []) {
      const key = localDateKey(s.departure_at);
      let g = map.get(key);
      if (!g) {
        g = { date: key, items: [], capacity: 0, sold: 0, remaining: 0, cancelled: 0 };
        map.set(key, g);
      }
      g.items.push(s);
      g.capacity += s.capacity;
      g.sold += s.reserved_count;
      g.remaining += Math.max(0, s.capacity - s.reserved_count);
      if (s.weather_cancelled) g.cancelled += 1;
    }
    return [...map.values()];
  }, [schedules.data]);

  const totalCount = schedules.data?.items.length ?? 0;

  const patchParams = (patch: Record<string, string | null>): void => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  const applyPreset = (preset: 'today' | 'next7' | 'month' | 'next30' | 'all'): void => {
    const t = todayStr();
    if (preset === 'today') patchParams({ from: t, to: t });
    else if (preset === 'next7') patchParams({ from: t, to: addDaysStr(t, 6) });
    else if (preset === 'next30') patchParams({ from: t, to: addDaysStr(t, 29) });
    else if (preset === 'all') patchParams({ from: null, to: null });
    else if (preset === 'month') {
      const d = new Date();
      const y = d.getFullYear();
      const m = d.getMonth();
      const first = `${y}-${pad(m + 1)}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      patchParams({ from: first, to: `${y}-${pad(m + 1)}-${pad(lastDay)}` });
    }
  };

  const presetBtn = (
    label: string,
    preset: 'today' | 'next7' | 'month' | 'next30' | 'all',
  ): JSX.Element => (
    <button type="button" onClick={() => applyPreset(preset)} style={presetStyle}>
      {label}
    </button>
  );

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">班次管理（A-2）</h1>
        <div className="page-head__actions">
          <Button variant="primary" onClick={() => navigate('/schedules/new')}>
            + 排班
          </Button>
          <Button
            variant="secondary"
            onClick={() => schedules.refetch()}
            disabled={!enabled || schedules.isFetching}
          >
            {schedules.isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">班次 CRUD（AC-A2-1）／批量建立（AC-A2-2）／天候停航（AC-A2-3）。</p>

      {/* ── 過濾列 ── */}
      <div style={filterBar}>
        <div style={filterRow}>
          <label style={filterLabel}>商品</label>
          <select
            value={productId}
            onChange={(e) => patchParams({ product_id: e.target.value || null })}
            disabled={products.isLoading}
            style={{ minWidth: 300 }}
          >
            <option value="">— 請選擇 —</option>
            {schedulableProducts(productOptions).map((p) => (
              <option key={p.id} value={p.id}>
                [{p.sku}] {p.name_i18n['zh-TW'] ?? p.name_i18n['en'] ?? '(未命名)'}
              </option>
            ))}
          </select>
          {products.error && (
            <span style={{ fontSize: 12, color: 'var(--c-danger)' }}>
              商品載入失敗：{(products.error as Error).message}
            </span>
          )}
        </div>

        <div style={filterRow}>
          <label style={filterLabel}>期間</label>
          <input type="date" value={from} onChange={(e) => patchParams({ from: e.target.value })} />
          <span style={{ color: '#6b7280' }}>～</span>
          <input type="date" value={to} onChange={(e) => patchParams({ to: e.target.value })} />
          <div style={{ display: 'flex', gap: 6, marginLeft: 4 }}>
            {presetBtn('今天', 'today')}
            {presetBtn('未來7天', 'next7')}
            {presetBtn('本月', 'month')}
            {presetBtn('未來30天', 'next30')}
            {presetBtn('全部', 'all')}
          </div>
        </div>

        <div style={filterRow}>
          <label style={filterLabel}>狀態</label>
          <select value={status} onChange={(e) => patchParams({ status: e.target.value })}>
            <option value="all">全部</option>
            <option value="active">僅營運</option>
            <option value="cancelled">僅停航</option>
          </select>
          {enabled && schedules.data && (
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--c-text-muted)' }}>
              共 {totalCount} 班 · {groups.length} 天
              {from === '' && to === '' ? '（全部期間）' : ''}
            </span>
          )}
        </div>
      </div>

      {!enabled && (
        <div style={{ color: 'var(--c-text-muted)', padding: '16px 0' }}>
          請先選擇商品以列出對應班次。
        </div>
      )}

      {enabled && schedules.isLoading && <div>載入中…</div>}
      {enabled && schedules.error && (
        <div className="error-banner">{(schedules.error as Error).message}</div>
      )}
      {toggleCancel.error && (
        <div className="error-banner">
          停航切換失敗：
          {toggleCancel.error instanceof ApiError
            ? toggleCancel.error.message
            : (toggleCancel.error as Error).message}
        </div>
      )}

      {enabled && schedules.data && (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th style={{ width: 150 }}>時段</th>
                <th style={{ textAlign: 'right' }}>容量</th>
                <th style={{ textAlign: 'right' }}>已售</th>
                <th style={{ textAlign: 'right' }}>剩餘</th>
                <th>狀態</th>
                <th style={{ width: 180 }}>動作</th>
              </tr>
            </thead>
            <tbody>
              {totalCount === 0 && (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    此條件下無班次（試試調整期間或按「全部」）
                  </td>
                </tr>
              )}
              {groups.map((g) => (
                <DayBlock
                  key={g.date}
                  group={g}
                  busyId={toggleCancel.isPending ? toggleCancel.variables?.id : undefined}
                  onEdit={(id) => navigate(`/schedules/${id}`)}
                  onToggle={(id, next) => toggleCancel.mutate({ id, next })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DayBlock({
  group,
  busyId,
  onEdit,
  onToggle,
}: {
  group: DayGroup;
  busyId: string | undefined;
  onEdit: (id: string) => void;
  onToggle: (id: string, next: boolean) => void;
}): JSX.Element {
  return (
    <>
      <tr>
        <td colSpan={6} style={dayHeaderCell}>
          <span style={{ fontWeight: 600 }}>
            {group.date}（{weekdayOf(group.date)}）
          </span>
          <span style={{ marginLeft: 12, fontSize: 12, color: '#6b7280' }}>
            {group.items.length} 班 · 容量 {group.capacity} · 已售 {group.sold} · 剩餘{' '}
            {group.remaining}
            {group.cancelled > 0 && (
              <span style={{ color: 'var(--c-danger)' }}> · 停航 {group.cancelled}</span>
            )}
          </span>
        </td>
      </tr>
      {group.items.map((s) => {
        const remaining = Math.max(0, s.capacity - s.reserved_count);
        const busy = busyId === s.id;
        return (
          <tr key={s.id}>
            <td style={{ fontVariantNumeric: 'tabular-nums' }}>
              {timeOf(s.departure_at)}–{timeOf(s.departure_end_at)}
            </td>
            <td style={{ textAlign: 'right' }}>{s.capacity}</td>
            <td style={{ textAlign: 'right' }}>{s.reserved_count}</td>
            <td style={{ textAlign: 'right' }}>{remaining}</td>
            <td>
              {s.weather_cancelled ? (
                <Pill tone="danger">停航</Pill>
              ) : (
                <Pill tone="success">營運</Pill>
              )}
            </td>
            <td>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button variant="secondary" onClick={() => onEdit(s.id)}>
                  編輯
                </Button>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => onToggle(s.id, !s.weather_cancelled)}
                >
                  {busy ? '處理中…' : s.weather_cancelled ? '取消停航' : '標記停航'}
                </Button>
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}

const filterBar: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  margin: '12px 0 18px',
  padding: 14,
  background: 'var(--c-surface, #f9fafb)',
  border: '1px solid var(--c-border, #e5e7eb)',
  borderRadius: 6,
};
const filterRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const filterLabel: React.CSSProperties = {
  width: 48,
  fontSize: 13,
  color: 'var(--c-text-muted)',
};
const presetStyle: React.CSSProperties = {
  fontSize: 12,
  padding: '4px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
};
const dayHeaderCell: React.CSSProperties = {
  background: 'var(--c-surface-2, #eef2f7)',
  padding: '8px 12px',
  borderTop: '2px solid var(--c-border, #e5e7eb)',
};
